import {MediaMatcher} from "@angular/cdk/layout";
import {isPlatformBrowser} from "@angular/common";
import type {OnInit} from "@angular/core";
import {Component, DOCUMENT, effect, inject, injectAsync, PLATFORM_ID, signal} from "@angular/core";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {RouterOutlet} from "@angular/router";
import {endWith, of, switchMap} from "rxjs";
import {equals} from "./helpers/Equatable";
import {Footer} from "./layout/footer/footer";
import {StandaloneLoadingIndicator} from "./layout/loading-indicator/loading-indicator";
import {Navbar} from "./layout/navbar/navbar";
import {ToastsOverlay} from "./layout/toasts/toasts-overlay";
import {CredentialsStorage} from "./services/credentials-storage";
import type {Theme} from "./services/theme-storage";
import {ThemeStorage} from "./services/theme-storage";

@Component({
  selector: "rf-app-root",
  templateUrl: "./app.html",
  styleUrl: "./app.scss",
  imports: [
    Footer,
    Navbar,
    RouterOutlet,
    StandaloneLoadingIndicator,
    ToastsOverlay,
  ],
})
export class App implements OnInit {
  private readonly usersApi = injectAsync(() => import("./services/api/users/users-api").then(m => m.UsersApi));
  private readonly credentialsStorage = inject(CredentialsStorage);

  // We replicate / untrack the credentials here to only do the logic in setupComplete once...
  private readonly lastCredentials = signal({
    isFirstRun: true,
    credentials: this.credentialsStorage.nonExpiredCredentials(),
  }, {equal: (l, r) => l.isFirstRun === r.isFirstRun && equals(l.credentials, r.credentials)});

  // This is not yet easily convertible to signals.
  // Otherwise, we do certain things multiple times (cause e.g., refreshing the token will trigger a second permission refresh).
  protected readonly setupComplete = toSignal(toObservable(this.lastCredentials).pipe(switchMap(data => {
    if (!data.credentials) {
      if (data.isFirstRun) return of(false);
      return this.credentialsStorage.readCredentialsFromWebApiIfNeeded().pipe(endWith(true));
    } else if (data.credentials.needsPasswordChange) {
      return of(true);
    } else if (data.credentials.isValid && !data.credentials.isAboutToExpire) {
      return this.credentialsStorage.refreshPermissions(this.usersApi).pipe(endWith(true));
    } else {
      return this.credentialsStorage.clearCredentials().pipe(endWith(true));
    }
  })), {initialValue: false});

  public constructor() {
    const platformId = inject(PLATFORM_ID);
    const document = inject(DOCUMENT);
    const themeStorage = inject(ThemeStorage);
    const mediaQuery = inject(MediaMatcher).matchMedia("(prefers-color-scheme: dark)");

    function setTheme(theme: Theme): void {
      document.documentElement.setAttribute("data-bs-theme", theme);
    }

    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    function themeChangeHandler(e: Readonly<MediaQueryListEvent>): void {
      setTheme(e.matches ? "dark" : "light");
    }

    effect(onCleanup => {
      const themeMode = themeStorage.themeMode();
      if (themeMode === "auto") {
        if (isPlatformBrowser(platformId)) {
          setTheme(mediaQuery.matches ? "dark" : "light");
          mediaQuery.addEventListener("change", themeChangeHandler);
          onCleanup(() => mediaQuery.removeEventListener("change", themeChangeHandler));
        } else {
          document.documentElement.removeAttribute("data-bs-theme");
        }
      } else {
        setTheme(themeMode);
        if (isPlatformBrowser(platformId)) mediaQuery.removeEventListener("change", themeChangeHandler);
      }
    });
  }

  public ngOnInit(): void {
    this.lastCredentials.set({isFirstRun: false, credentials: this.credentialsStorage.nonExpiredCredentials()});
  }
}
